import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import {
  type RemoveSectionFromTemplateMutation,
  type RemoveSectionFromTemplateMutationVariables,
} from 'graphql-query-contracts';
import { removeSectionFromTemplateMutation } from 'graphql-queries';

import { RemoveSectionFromTemplateError } from './errors/RemoveSectionFromTemplateError';

export type RemoveSectionFromTemplateParams = {
  channelId: string;
  templateId: string;
  sectionId: string;
};

export type RemoveSectionFromTemplateResult = {
  success: boolean;
  newVersion: number;
};

export type UseRemoveSectionFromTemplateResult = {
  removeSectionFromTemplate: (
    params: RemoveSectionFromTemplateParams
  ) => Promise<RemoveSectionFromTemplateResult>;
  loading: boolean;
  error: Error | undefined;
};

function validateParams(params: RemoveSectionFromTemplateParams): void {
  if (!params.channelId?.trim()) {
    throw new RemoveSectionFromTemplateError('channelId is required');
  }

  if (!params.templateId?.trim()) {
    throw new RemoveSectionFromTemplateError('templateId is required');
  }

  if (!params.sectionId?.trim()) {
    throw new RemoveSectionFromTemplateError('sectionId is required');
  }
}

export const useRemoveSectionFromTemplate =
  (): UseRemoveSectionFromTemplateResult => {
    const [removeSection, { loading }] = useMutation<
      RemoveSectionFromTemplateMutation,
      RemoveSectionFromTemplateMutationVariables
    >(removeSectionFromTemplateMutation);

    const [error, setError] = useState<Error | undefined>(undefined);

    const removeSectionFromTemplate = useCallback(
      async (
        params: RemoveSectionFromTemplateParams
      ): Promise<RemoveSectionFromTemplateResult> => {
        try {
          setError(undefined);

          validateParams(params);

          const variables: RemoveSectionFromTemplateMutationVariables = {
            input: {
              channelId: params.channelId,
              templateId: params.templateId,
              sectionId: params.sectionId,
            },
          };

          const mutationResult = await removeSection({ variables });

          const payload = mutationResult.data?.removeSectionFromTemplate;

          if (!payload) {
            throw new RemoveSectionFromTemplateError();
          }

          if (!payload.success) {
            throw new RemoveSectionFromTemplateError(
              'Remove section operation did not succeed'
            );
          }

          return {
            success: payload.success,
            newVersion: payload.newVersion,
          };
        } catch (err) {
          const wrappedError =
            err instanceof RemoveSectionFromTemplateError
              ? err
              : new RemoveSectionFromTemplateError(
                  err instanceof Error
                    ? err.message
                    : 'Failed to remove section from inspection template'
                );

          setError(wrappedError);

          throw wrappedError;
        }
      },
      [removeSection]
    );

    return {
      removeSectionFromTemplate,
      loading,
      error,
    };
  };
